import { authAPI, HandleError } from '../core/instance';
import {
  GetAssignmentListResponse,
  GetStudyContentListResponse,
} from './dashboard.interface';
import { getUrlByQueryParameterObj } from '@asset/function/queryStringFunctions';
import _ from 'lodash';
import {
  MaterialType,
  StudyContentListOrderBy,
  StudyContentType,
  SubjectEnum,
  TotalCourse,
} from '@asset/enum/materialEnum';
import {
  AnomalyStatus,
  AnomalyType,
  AssignmentType,
  FeedbackCheckStatus,
  MyContentType,
  RubricOverallGradingResult,
} from '@asset/enum/studyContentEnum';
import { StudyRecordsResponse } from '@asset/type/dashboard';
import qs from 'qs';
import { Grade, WeekDay } from '@asset/enum/enum';
import {
  StudentStatus,
  StudentTeacherMappingType,
  TeacherPosition,
  TeacherStatus,
} from '@asset/enum/UserEnum';
import { CommonResponse } from '@apis/common/response';
import { StudentStatusEvent, TeacherInformation } from '@asset/type/userType';
import {
  ProblemStatusType,
  SolvingResult,
  StudyItemType,
  StudyStep,
} from '@asset/enum/problemSolvingEnum';
import { Status } from '@asset/type/common/type';
import { StudentMetaInfoV2 } from '@asset/type/userType';
import { QuestionStatus } from '@asset/enum/studyStatus';
import { PageBasedPagination } from '@asset/type/common/response';
import { SchoolInfo } from './school';

export const getTeacherInfoData = async (
  teacherId: number,
): Promise<
  TeacherInformation & {
    teacherCode: string;
  }
> => {
  const data = await authAPI
    .get(`/api/teachers/teacher-info/${teacherId}/`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export type GetTeacherPageListQuery = {
  pageSize?: number;
  page?: number;
  search?: string;
  teacherIdList?: number[];
  branchNameList?: string[];
  branchCategoryList?: string[];
  positionList?: TeacherPosition[];
  statusList?: string[];
  pageType?: 'side-bar';
  orderBy?: '-id' | 'id' | '-name' | 'name';
};

export const getTeacherPageList = async <T extends GetTeacherPageListQuery>(
  queryStringObj: T,
): Promise<
  T['pageType'] extends 'side-bar'
    ? { id: number; name: string }[]
    : CommonResponse & {
        pagination: {
          totalTeacherCount: number;
          nextPage: boolean;
          previousPage: boolean;
        };
        teachers: TeacherInformation[];
      }
> => {
  let url = `/api/teachers/teacher-info/`;
  if (queryStringObj) {
    const queryString = qs.stringify(queryStringObj, {
      encodeValuesOnly: true,
      arrayFormat: 'comma',
      skipNulls: true,
    });
    url += `?${queryString}`;
  }

  const data = await authAPI.get(url).then((response) => {
    return response.data;
  });
  return data;
};

export interface StudyStatus {
  studentId: number;
  studentName: string;
  teacherName: string;
  materialName: string;
  problemNumber: string;
  solvedAt: string;
  solvingResult: Status;
  rubricGradeOverallResult: RubricOverallGradingResult;
  feedbackCheckStatus: FeedbackCheckStatus;
  pageId: number;
  studyStatusType: string;
  problemStatusDetailId: number;
  problemStatusId: number;
  studyItemType: StudyItemType;
  studyMaterial: {
    id: number;
    subject: SubjectEnum;
    type: MaterialType;
    course: TotalCourse;
    name: string;
  };
  assignments: {
    id: number;
    title: string;
    type: AssignmentType;
  }[];
  isAnomalous: boolean;
  anomalies: {
    id: number;
    anomalyType: AnomalyType;
    status: AnomalyStatus;
  }[];
  problemStatusType: ProblemStatusType;
  hasWarning: boolean;
  questionStatus: QuestionStatus;
}

export interface TotalStudyStatusDataResponse extends CommonResponse {
  pagination: {
    totalCount: number;
    page: number;
    nextPage: boolean;
    previousPage: boolean;
  };
  totalStudyList: StudyStatus[];
}

export const getTotalStudentStudyStatusList = async (queryObj: {
  page: number;
  pageSize?: number;
  teacher?: number[];
  student?: number[];
  studyMaterialCourses?: TotalCourse[];
  assignmentTypes?: AssignmentType[];
  solvingResult?: SolvingResult[];
  studyStatusTypeList?: StudyStep[];
  problemStatusTypes?: ProblemStatusType[];
  rubricGradeOverallResults?: RubricOverallGradingResult[];
  questionStatuses?: QuestionStatus[];
  feedbackCheckStatuses?: FeedbackCheckStatus[];
  isAnomalous?: boolean[];
  solvedAt?: 'asc' | 'desc';
}): Promise<TotalStudyStatusDataResponse> => {
  const url = `/api/teachers/student-study-status/total/?${decodeURIComponent(
    qs.stringify(queryObj, {
      skipNulls: true,
      arrayFormat: 'comma',
    }),
  )}`;
  const data = await authAPI
    .get<TotalStudyStatusDataResponse>(url)
    .then((response) => {
      return response.data;
    });
  return data;
};

export const getStudentList = async (query: {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: StudentStatus;
  // [DEPRECATED] 대신 teacherIds 사용
  // ----------------------------
  teacherId?: number;
  // ----------------------------
  teacherIds?: number[];
}): Promise<{
  success: boolean;
  pagination: PageBasedPagination;
  students: StudentMetaInfoV2[];
}> => {
  const queryString = getUrlByQueryParameterObj('', query);
  const studentList = await authAPI
    .get(`/api/students/${queryString}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });

  return studentList;
};
export const getAssignmentList = async (
  studentId,
  pageNumber = 1,
  pageSize = 15,
  showHidden = false,
  startDateTime = null,
  endDateTime = null,
) => {
  let apiUrl = `/api/students/${studentId}/my-assignments/?page=${pageNumber}&pageSize=${pageSize}&showHidden=${showHidden}`;
  if (startDateTime && endDateTime) {
    apiUrl += `&startDateTime=${encodeURIComponent(
      startDateTime,
    )}&endDateTime=${encodeURIComponent(endDateTime)}`;
  }
  const data = await authAPI
    .get<GetAssignmentListResponse>(apiUrl)
    .then((response) => {
      return response.data;
    });
  return data;
};

export const getStudyContentList = async (
  studentId: number,
  studyContentType:
    | StudyContentType.MY_STUDY_MATERIAL
    | StudyContentType.MY_TEST,
  pageNumber = 1,
  pageSize = 15,
  isHidden: boolean | null = null,
  studyMaterialCourse: string | null = null,
  hasAssignment: boolean | null = null,
  search: string | null = null,
  isBookmarked: boolean | null = null,
  orderBy: StudyContentListOrderBy[] | StudyContentListOrderBy = null,
): Promise<GetStudyContentListResponse> => {
  let apiUrl = `/api/study-data/my-contents/?studentId=${studentId}&studyType=${studyContentType}&page=${pageNumber}&pageSize=${pageSize}`;
  if (!_.isNull(isHidden)) apiUrl += `&isHidden=${isHidden}`;
  if (studyMaterialCourse)
    apiUrl += `&studyMaterialCourse=${studyMaterialCourse}`;
  if (!_.isNull(hasAssignment)) apiUrl += `&hasAssignment=${hasAssignment}`;
  if (search) apiUrl += `&search=${search}`;
  if (!_.isNull(isBookmarked)) apiUrl += `&isBookmarked=${isBookmarked}`;
  if (!_.isNull(orderBy)) apiUrl += `&orderBy=${orderBy}`;

  const studyContentList = await authAPI
    .get<GetStudyContentListResponse>(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return studyContentList;
};

export async function getStudyRecordsDataV2(
  studentId: number,
  startDateTime: string,
  endDateTime: string,
  timezoneOffset: number,
): Promise<StudyRecordsResponse> {
  const apiUrl = `/api/students/${studentId}/my-study-records/?${qs.stringify({
    startDateTime,
    endDateTime,
    timezoneOffset,
  })}`;

  const data = await authAPI
    .get(apiUrl)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
}

export async function getStudyRecordsData(
  studentId: number,
  startDateTimeStamp: number,
  endDateTimeStamp: number,
  timezoneOffset: number,
): Promise<StudyRecordsResponse> {
  const data = await authAPI
    .get(
      `/api/students/${studentId}/my-study-records/?startDate=${startDateTimeStamp}&endDate=${endDateTimeStamp}&timezoneOffset=${timezoneOffset}`,
    )
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
}

export const getSchoolNameList = async () => {
  const data = await authAPI.get(`/api/students/schools/`).then((response) => {
    return response.data;
  });
  return data;
};

export const getSchoolListV2 = async (): Promise<{
  success: boolean;
  data: {
    schools: Pick<SchoolInfo, 'id' | 'name' | 'area' | 'subArea'>[];
  };
}> => {
  const data = await authAPI
    .get(`/api/students/v2/schools/`)
    .then((response) => {
      return response.data;
    });
  return data;
};

export const getMaterialName = async (
  subject: string,
  course: string,
  materialType: string,
): Promise<
  {
    materialId: number;
    materialName: string;
    materialType: MaterialType;
  }[]
> => {
  let url = `/api/materials/autocomplete/`;
  if (subject || course || materialType) {
    const queryString = getUrlByQueryParameterObj('', {
      subject,
      course,
      materialType,
    });
    url += queryString;
  }
  const data = await authAPI
    .get(url)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const getMaterialCourseList = async (
  subject: string,
): Promise<{ courseName: TotalCourse }[]> => {
  const data = await authAPI
    .get(`/api/materials/autocomplete/?subject=${subject}`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const getStudentCode = async (studentId) => {
  const data = await authAPI
    .get(`/api/teachers/student-code/${studentId}/`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export type StudentInformation = {
  name: string;
  gender: '여' | '남' | '미정';
  grade: Grade;
  schoolName: string;
  birthDate?: string;
  phoneNumber: string;
  acquisitionChannel: string;
  parentPhoneNumber: string;
  parents: {
    id: number;
    phoneNumber: string;
  }[];
  diligence: number;
  gradePoint: number;
  studyMotivation: number;
  memo: string;
  language: 'ko' | 'en';
  country: string;
  city: string;
  timezone: string;
  userEmail: string;
  teachers: {
    id: number;
    name: string;
    mappingType: StudentTeacherMappingType;
  }[];
  teacherId: number;
  teacherName: string;
  studyDaysOfWeek: WeekDay[];
  isSpecialClass: boolean;
  status: string;
  invitationLink: string;
  latestStatusChangeEvent: StudentStatusEvent | null;
  school: SchoolInfo | null;
};
export const getStudentInfo = async (
  studentId: number,
): Promise<StudentInformation> => {
  const data = await authAPI
    .get(`/api/teachers/student-info/${studentId}/`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const updateStudentInfoGeoApi = async (studentId, reqBody) => {
  const data = await authAPI
    .patch(`/api/teachers/student-geo-language-info/${studentId}/`, reqBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const postMyStudyContent = async (
  studentId,
  reqData,
): Promise<{
  success: boolean;
  newObject: {
    id: number;
    materialName: string;
    imageUrl: string;
    studyType: StudyContentType;
    myContentType: MyContentType;
    totalProblemCount: number;
    progressRate: number;
    correctRate: number;
    startPage: number;
    lastPage: number;
  };
  message?: string;
}> => {
  const data = await authAPI
    .post(`/api/students/${studentId}/my-study-materials/`, reqData)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const patchStudentInfo = async (studentId, reqData) => {
  const data = await authAPI
    .patch(`/api/teachers/student-info/${studentId}/`, reqData)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const postStudentInfo = async (
  reqBody: {
    name: string;
    grade: string;
    schoolId: number;
    phoneNumber?: string;
    parentPhoneNumbers: string[];
    language: string;
    timezone: string;
    country: string;
    city: string;
  } | null = null,
): Promise<{
  success: boolean;
  message: string;
  newStudentId: number;
}> => {
  const data = await authAPI
    .post(`/api/teachers/student-info/`, reqBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const getStudentListApi = async (
  parentIds: number[] = null,
  config: {
    handleError: HandleError;
  } = null,
): Promise<
  {
    id: number;
    name: string;
  }[]
> => {
  const apiUrl = `/api/teachers/student-info/?${qs.stringify(
    { parentIds: parentIds ? parentIds.toString() : null },
    {
      skipNulls: true,
    },
  )}`;

  const data = await authAPI
    .get(apiUrl, config)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

// TODO : 후에 v2로 변경
export const postTeacherInfo = async (reqData: {
  name: string;
  branchName: string;
  position: TeacherPosition;
  phoneNumber: string;
  status: TeacherStatus;
}) => {
  const data = await authAPI
    .post(`/api/teachers/teacher-info/`, reqData)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const getStudyMaterialRecord = async (
  studyContentId,
  startDate,
  endDate,
  classificationId,
  classificationType,
  timezoneOffset,
) => {
  const data = await authAPI
    .get(
      `/api/students/study-report/toggle/${studyContentId}/?startDate=${startDate}&endDate=${endDate}&timezoneOffset=${timezoneOffset}${
        classificationId
          ? '&currentClassificationId=' +
            classificationId +
            '&currentClassificationType=' +
            classificationType
          : ''
      }`,
    )
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export interface MoteMoteCalendarData {
  success: boolean;
  calendar: Record<string, number[]>;
}

export const getMoteMotePlanListData = async (
  studentId,
  timezoneOffset,
  startDate = null,
  endDate = null,
): Promise<MoteMoteCalendarData> => {
  let api = `/api/students/motemote/?studentId=${studentId}&timezoneOffset=${timezoneOffset}`;
  if (startDate) {
    api += `&startDate=${startDate}&endDate=${endDate}`;
  }
  const data = await authAPI
    .get(api)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
export const patchNewPassword = async (reqData) => {
  const data = await authAPI
    .patch(`/api/users/password/`, reqData)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const changeErrorReportIsResolved = async (isResolved, reportId) => {
  const data = await authAPI
    .get(
      `/api/study-data/error-report/?isResolved=${isResolved}&reportId=${reportId}`,
    )
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const updateStudentEmail = async (reqBody) => {
  const data = authAPI
    .post(`/api/teachers/student/change-email/save/`, reqBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const validateStudentEmail = async (reqBody) => {
  const data = authAPI
    .post(`/api/teachers/student/change-email/validation/`, reqBody)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};

export const getDailyReportApi = async (studentId, startOfDay) => {
  const data = await authAPI
    .get(
      `/api/study-data/daily-report/?studentId=${studentId}&startOfDay=${startOfDay}`,
    )
    .then((response) => {
      return response.data;
    });
  return data;
};
export const getMyContentsClassifications = async (
  studentId: number,
  studyContentType:
    | StudyContentType.MY_STUDY_MATERIAL
    | StudyContentType.MY_TEST,
  classificationType: 'studyMaterialCourse' | 'subject',
  isHidden: boolean | null = null,
  hasAssignment: boolean | null = null,
  search: string | null = null,
): Promise<CommonResponse & { classifications: TotalCourse[] }> => {
  let apiUrl = `/api/study-data/my-contents-classifications/?studentId=${studentId}&studyType=${studyContentType}&classificationType=${classificationType}`;
  if (!_.isNull(isHidden)) apiUrl += `&isHidden=${isHidden}`;
  if (!_.isNull(hasAssignment)) apiUrl += `&hasAssignment=${hasAssignment}`;
  if (search) apiUrl += `&search=${search}`;
  const data = await authAPI.get(apiUrl).then((response) => {
    return response.data;
  });
  return data;
};
export const putMyStudyContentBookmark = async (
  myContentId: number,
  requestData: {
    isBookmarked: boolean;
  },
) => {
  const data = await authAPI
    .put(`/api/study-data/my-contents/${myContentId}/bookmark/`, requestData)
    .then((response) => {
      return response.data;
    });
  return data;
};

export const sendInvitationLink = async (studentId: number) => {
  const data = await authAPI
    .post(`/api/students/${studentId}/invitations`)
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      return error.response.data;
    });
  return data;
};
